import { useCallback, useMemo } from "react";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomSelectActions from "@/components/desktop/list/select-actions/CustomSelectActions";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";
import OACalendarEventCard from "./components/calendar/OACalendarEventCard";

import { getApplicationOrderStateVariant } from "@/constants/applicationOrderStates";
import { getApplicationOrderTypeVariant } from "@/constants/applicationOrderTypes";

import { useUserScreenPreferences, useSaveUserScreenPreferences } from "@/hooks/general/useUserScreenPreferences";

import useOAController from "./hooks/controller/useOAController";
import useOACalendarFilters from "./hooks/filters/useOACalendarFilters";
import useOAFilterQueryState from "./hooks/filters/useOAFilterQueryState";
import useOASyncFilterQueryState from "./hooks/filters/useOASyncFilterQueryState";
import useOARouteActions from "./hooks/routing/useOARouteActions";
import useOAViewMode from "./hooks/view/useOAViewMode";

const OA = () => {
	const { initialBaseFilter, hasExplicitDateFilters } = useOAFilterQueryState();

	const {
		navigate,
		isLoadingPermissions,
		canShow,
		canCreate,
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		reactTableSort,
		isLoading,
		isFetching,
		tableData,
		response,
		columns,
		selectActions,
		selectActionIconMap,
		selectedRowsCount,
		tableActionButtons,
		showCopyOAConfirm,
		setShowCopyOAConfirm,
		handleDuplicateSelectedOA,
		handleCreateOA,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		applyFilterOverrides,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
	} = useOAController({
		initialBaseFilter,
		skipAutoDateInit: hasExplicitDateFilters,
	});

	useOASyncFilterQueryState(appliedFiltersState);

	const preferencesEnabled = !isLoadingPermissions && canShow;
	const {
		data: screenPreferences,
		isLoading: isLoadingPreferences,
		isFetched: isPreferencesFetched,
	} = useUserScreenPreferences("oa_list", {
		enabled: preferencesEnabled,
	});
	const { mutate: savePreferences } = useSaveUserScreenPreferences("oa_list");
	const { openPreview } = useOARouteActions();
	const { applyCalendarRange, handleCalendarMonthChange } = useOACalendarFilters(applyFilterOverrides);
	const { viewMode, handleViewModeChange } = useOAViewMode({
		preferencesEnabled,
		isPreferencesFetched,
		isLoadingPreferences,
		screenPreferences,
		savePreferences,
		onCalendarMode: () => applyCalendarRange(new Date(), { season_id: "" }),
	});

	const handleCalendarEventClick = useCallback(
		(eventItem) => {
			const nextPreviewCode = eventItem?.app_order_id || eventItem?.id || null;
			if (nextPreviewCode) {
				openPreview(nextPreviewCode);
			}
		},
		[openPreview],
	);

	const calendarEventConfig = useMemo(
		() => ({
			primary: { accessor: "code", prefix: "N° " },
			secondary: { accessor: "estate_parent_name" },
			badges: [
				{
					accessor: "application_orders_state_name",
					variantAccessor: "application_orders_state_id",
					variant: (stateId) => getApplicationOrderStateVariant(Number(stateId)),
				},
				{
					accessor: "application_order_type_customer_name",
					variantAccessor: "application_order_type_id_customer",
					variant: (typeId) => getApplicationOrderTypeVariant(Number(typeId)),
				},
			],
		}),
		[],
	);

	const renderCalendarEvent = useCallback((eventItem) => <OACalendarEventCard record={eventItem} eventConfig={calendarEventConfig} />, [calendarEventConfig]);

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<>
			<ListScaffold
				titleBreadcrumbs={[
					{ label: "Dashboard", onClick: () => navigate("/") },
					{ label: "Planificación", onClick: () => navigate("/planificacion") },
					{ label: "Órdenes de Aplicación", onClick: () => navigate("/planificacion/ordenes_aplicacion") },
				]}
				subtitle="Planifica y consulta tus órdenes de aplicación: revisa fechas, productos y estado de cada operación de campo."
				showCreate={canCreate}
				createLabel="Nueva OA"
				onCreate={handleCreateOA}
				topContent={selectedRowsCount > 0 ? <CustomSelectActions actions={selectActions} iconMap={selectActionIconMap} /> : null}
				actionButtons={tableActionButtons}
				filterTitle="Filtrar Órdenes de Aplicación"
				isFilterOpen={isFilterOpen}
				filters={filtersState}
				appliedFilters={appliedFiltersState}
				onFilterChange={handleFilterChange}
				onApplyFilters={handleApplyFilters}
				onClearFilters={handleClearAllFilters}
				onClearOneFilter={handleClearOneFilter}
				onOpenFilter={openFilter}
				onCloseFilter={closeFilter}
				isFetching={isFetching}
				isLoading={isLoading}
				data={tableData}
				totalItems={response?.totalRecords || 0}
				totalPages={response?.totalPages || 0}
				toItem={Math.min(tableState.page * tableState.per_page, response?.totalRecords || 0)}
				fromItem={response?.totalRecords ? (tableState.page - 1) * tableState.per_page + 1 : 0}
				pageIndex={tableState.page}
				columns={columns}
				onPageChange={handlePageChange}
				pageSize={tableState.per_page}
				onPageSizeChange={handlePageSizeChange}
				sortBy={reactTableSort}
				onSortByChange={handleSortByChange}
				pageSizeOptions={[20, 50, 100]}
				initialPageSize={tableState.per_page}
				initialSortBy={reactTableSort}
				compactHeaders={true}
				compactBody={true}
				viewMode={viewMode}
				onViewModeChange={handleViewModeChange}
				enableCalendarView={true}
				calendarDateAccessor="application_order_date"
				calendarEventConfig={calendarEventConfig}
				onCalendarEventClick={handleCalendarEventClick}
				onCalendarMonthChange={handleCalendarMonthChange}
				renderCalendarEvent={renderCalendarEvent}
			/>
			<CustomConfirmModal
				open={showCopyOAConfirm}
				onClose={() => setShowCopyOAConfirm(false)}
				onConfirm={handleDuplicateSelectedOA}
				title="Confirmar acción"
				message="¿Está seguro que desea copiar las órdenes seleccionadas?"
				confirmText="Sí, continuar"
				cancelText="Cancelar"
			/>
		</>
	);
};

export default OA;
